import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, PageState } from '../components/ui';
import { ApiClientError } from '../api/client';
import { useVerifyEmail } from '../api/auth';
import { useAuth } from './AuthContext';

/**
 * Truly public (like /privacy) - reachable whether or not the browser has a
 * live session, since the emailed link may be opened on a different device
 * or after signing out. If this browser *is* signed in, refreshes the
 * session so the "verify your email" banner disappears immediately.
 */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const verifyEmail = useVerifyEmail();
  const { state, refreshMe } = useAuth();
  const [result, setResult] = useState<'pending' | 'success' | 'error'>('pending');
  const [message, setMessage] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current || !token) return;
    started.current = true;
    verifyEmail.mutate(
      { token },
      {
        onSuccess: () => {
          setResult('success');
          if (state.status === 'authenticated') void refreshMe();
        },
        onError: (err) => {
          setResult('error');
          setMessage(
            err instanceof ApiClientError && err.code === 'AUTH_TOKEN_INVALID'
              ? 'This verification link is invalid or has expired.'
              : 'Could not verify your email. Please try again.',
          );
        },
      },
    );
  }, [token]);

  return (
    <div className="ne-auth">
      <Card className="ne-auth__card">
        <h1 className="ne-auth__title">Verify email</h1>
        {!token ? (
          <PageState
            kind="error"
            title="Invalid link"
            message="This verification link is missing its token."
          />
        ) : result === 'pending' ? (
          <PageState kind="loading" message="Verifying your email…" />
        ) : result === 'success' ? (
          <PageState kind="empty" title="Email verified" message="You're all set." />
        ) : (
          <PageState kind="error" title="Verification failed" message={message ?? undefined} />
        )}
        <p className="ne-auth__foot">
          {state.status === 'authenticated' ? (
            <Link to="/">Back to dashboard</Link>
          ) : (
            <Link to="/login">Back to sign in</Link>
          )}
        </p>
      </Card>
    </div>
  );
}
